import MarkdownIt from 'markdown-it'

import type { ChatbotEntity } from '@/services/chatbot-service/models'
import { escapeHtml } from './html'

// Anchored to the start of the slice passed from state.pos.
const ENTITY_TAG_RE = /^<(vehicle|group) id="([^"]+)"\s*\/>/

const md = new MarkdownIt({
  html: false,   // never pass raw HTML through
  linkify: true, // bare URLs → <a>
  breaks: true,  // \n → <br>
})

// Differentiate internal vs external links:
// - same origin → data-internal, intercepted by Vue Router (no full reload)
// - external    → target="_blank" rel="noopener noreferrer"
const defaultLinkOpen =
  md.renderer.rules.link_open ??
  ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options))

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx]
  const href = token.attrGet('href') ?? ''

  try {
    const url = new URL(href, window.location.href)
    if (url.origin === window.location.origin) {
      token.attrSet('data-internal', url.pathname + url.search + url.hash)
    } else {
      token.attrSet('target', '_blank')
      token.attrSet('rel', 'noopener noreferrer')
    }
  } catch {
    // Unparseable href — treat as external to be safe
    token.attrSet('target', '_blank')
    token.attrSet('rel', 'noopener noreferrer')
  }

  return defaultLinkOpen(tokens, idx, options, env, self)
}

// Inline rule: intercept <vehicle id="..." /> and <group id="..." /> before the
// text accumulator collects it. Inline rules only run inside paragraphs/headings/
// etc. — block-level code (fence, code_block) and inline code (backtick) bypass
// inline parsing entirely, so entity tags inside code are never touched. No
// renderer override needed.
md.inline.ruler.push('entity_chip', (state, silent) => {
  if (state.src.charCodeAt(state.pos) !== 0x3C /* < */) return false

  const match = ENTITY_TAG_RE.exec(state.src.slice(state.pos))
  if (!match) return false

  if (!silent) {
    const entities: ChatbotEntity[] = state.env?.entities ?? []
    const [, tagType, id] = match
    const entity = entities.find((e) => e.id === id)
    const name = entity?.name ?? id

    // A vehicle id is its serial number: expose it for click-to-copy. The hover
    // tooltip is rendered by ChatbotMessage from this attribute — no native
    // `title` here. Group ids are internal — no copy.
    const copyAttrs = tagType === 'vehicle' ? ` data-sn="${escapeHtml(id)}"` : ''

    const token = state.push('html_inline', '', 0)
    token.content = `<span class="entity-chip"${copyAttrs}>${escapeHtml(name)}</span>`
    token.markup = match[0]
  }

  state.pos += match[0].length
  return true
})

/**
 * Renders a chatbot message to safe HTML.
 * Vehicle and group tags are replaced with chip spans only in inline text —
 * code fences and inline code pass through unchanged.
 */
export function renderChatbotMessage(content: string, entities: ChatbotEntity[]): string {
  return md.render(content, { entities })
}
